import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * ARON workbench — the VS Code-like shell of the full ARON page: conversations
 * are open as TABS, tabs live in editor GROUPS, and groups tile the page as a
 * TREE of splits. The tree is what makes the layout behave: a split is created
 * next to the pane you dropped on, with its own direction, so splitting a pane
 * sideways inside a stacked layout stays INSIDE that pane instead of re-flowing
 * the whole page. The layout is persisted, so the set of open conversations
 * survives a reload exactly like an editor session.
 *
 * Two signals ride on the same state, because both are things ARON needs FROM the
 * user and both must be visible on a tab that is not currently on screen:
 *  - `attention` — ARON finished a turn in a background tab (it asked a question,
 *    it proposed a write, or it simply answered),
 *  - `approval` — ARON proposed a database write; the modal gate that pops for it.
 */

export type AronAttention = "question" | "write" | "done";

export interface AronTab {
  /** Stable tab identity — survives the draft → created-conversation swap. */
  key: string;
  /** Conversation id; null = a draft tab (nothing created server-side yet). */
  convId: string | null;
  /**
   * Podgląd CUDZEJ rozmowy (audyt CEO+DEV): karta czyta transkrypt przez
   * /aron/admin/conversations/{id}/ i NIE ma czatu — nie wolno w niej pisać,
   * dociągać wiadomości ani drukować własnymi endpointami właściciela wątku
   * (te zwróciłyby 404, bo lista rozmów jest twardo cięta do zalogowanego).
   */
  readOnly?: boolean;
  /** Tytuł karty podglądu — bierzemy go z wiersza listy audytu, bo tytułu
   * cudzej rozmowy nie da się dociągnąć zwykłym endpointem. */
  title?: string;
}

export type AronSplitOrientation = "row" | "column";

export interface AronGroupNode {
  type: "group";
  id: string;
  tabs: AronTab[];
  activeKey: string | null;
  /** Flex weight among the siblings inside the parent split. */
  size: number;
}

export interface AronSplitNode {
  type: "split";
  id: string;
  orientation: AronSplitOrientation;
  size: number;
  children: AronNode[];
}

export type AronNode = AronGroupNode | AronSplitNode;

/** Where a tab was let go over a group's body — the VS Code drop zones. */
export type AronDropZone = "center" | "left" | "right" | "top" | "bottom";

export interface AronApprovalRequest {
  convId: string;
  /** API base of the persona that proposed it ("/aron" | "/eng-aron") — the
   * modal is mounted globally, outside any AronApiBaseContext provider. */
  apiBase: string;
}

/** Smallest share of a split one pane can be dragged down to. */
export const MIN_GROUP_SIZE = 0.12;

let seq = 0;
const uid = (prefix: string) =>
  `${prefix}${++seq}-${Math.random().toString(36).slice(2, 8)}`;

export function makeTab(
  convId: string | null = null,
  extra: Omit<AronTab, "key" | "convId"> = {},
): AronTab {
  return { key: uid("t"), convId, ...extra };
}

export function makeGroup(tabs: AronTab[]): AronGroupNode {
  return {
    type: "group",
    id: uid("g"),
    tabs,
    activeKey: tabs.length ? tabs[tabs.length - 1].key : null,
    size: 1,
  };
}

// ── Tree helpers (pure — the layout is one immutable tree) ───────────────────

/** Every group in the tree, in visual order. */
export function collectGroups(node: AronNode | null): AronGroupNode[] {
  if (!node) return [];
  return node.type === "group" ? [node] : node.children.flatMap(collectGroups);
}

export function findGroup(node: AronNode | null, id: string): AronGroupNode | null {
  return collectGroups(node).find((g) => g.id === id) ?? null;
}

/** Replace one group in place (tab list / active pointer changes). */
function updateGroup(
  node: AronNode,
  id: string,
  fn: (g: AronGroupNode) => AronGroupNode,
): AronNode {
  if (node.type === "group") return node.id === id ? fn(node) : node;
  return { ...node, children: node.children.map((c) => updateGroup(c, id, fn)) };
}

/**
 * Put `group` next to `targetId`, along `orientation`.
 *
 * A split that already runs that way takes the new pane as a sibling; otherwise
 * the target pane alone is wrapped in a fresh split. That local wrap is what
 * keeps a sideways split inside one pane of a stacked layout from re-flowing
 * every other pane on the page.
 */
function insertBeside(
  node: AronNode,
  targetId: string,
  group: AronGroupNode,
  orientation: AronSplitOrientation,
  before: boolean,
): AronNode {
  if (node.type === "group") {
    if (node.id !== targetId) return node;
    const target = { ...node, size: 1 };
    const added = { ...group, size: 1 };
    return {
      type: "split",
      id: uid("s"),
      orientation,
      size: node.size || 1,
      children: before ? [added, target] : [target, added],
    };
  }
  const idx = node.children.findIndex((c) => c.type === "group" && c.id === targetId);
  if (idx !== -1 && node.orientation === orientation) {
    const target = node.children[idx];
    const half = (target.size || 1) / 2;
    const children = node.children.slice();
    children[idx] = { ...target, size: half };
    children.splice(before ? idx : idx + 1, 0, { ...group, size: half });
    return { ...node, children };
  }
  return {
    ...node,
    children: node.children.map((c) =>
      insertBeside(c, targetId, group, orientation, before),
    ),
  };
}

/** Drop empty groups, collapse one-child splits, repair active pointers. */
function normalizeTree(node: AronNode): AronNode | null {
  if (node.type === "group") {
    if (node.tabs.length === 0) return null;
    const valid = node.activeKey && node.tabs.some((t) => t.key === node.activeKey);
    return valid ? node : { ...node, activeKey: node.tabs[node.tabs.length - 1].key };
  }
  const children = node.children
    .map(normalizeTree)
    .filter((c): c is AronNode => c !== null);
  if (children.length === 0) return null;
  if (children.length === 1) return { ...children[0], size: node.size };
  return { ...node, children };
}

/** Remove a whole group (with its tabs) from the tree. */
function removeGroup(node: AronNode, id: string): AronNode | null {
  if (node.type === "group") return node.id === id ? null : node;
  const children = node.children
    .map((c) => removeGroup(c, id))
    .filter((c): c is AronNode => c !== null);
  if (children.length === 0) return null;
  if (children.length === 1) return { ...children[0], size: node.size };
  return { ...node, children };
}

/** Resize any node (group or split) by id — the divider drag writes here. */
function applySizes(node: AronNode, sizes: Record<string, number>): AronNode {
  const size = sizes[node.id] ?? node.size;
  if (node.type === "group") return size === node.size ? node : { ...node, size };
  return { ...node, size, children: node.children.map((c) => applySizes(c, sizes)) };
}

/** Whole-state repair after any structural change — the workbench is never left
 * without somewhere to type. */
function finalize(
  root: AronNode | null,
  activeGroupId: string,
  maximizedGroupId: string | null,
): Pick<AronWorkbenchState, "root" | "activeGroupId" | "maximizedGroupId"> {
  const next = (root && normalizeTree(root)) || makeGroup([makeTab(null)]);
  const groups = collectGroups(next);
  return {
    root: next,
    activeGroupId: groups.some((g) => g.id === activeGroupId)
      ? activeGroupId
      : groups[0].id,
    maximizedGroupId:
      maximizedGroupId && groups.some((g) => g.id === maximizedGroupId)
        ? maximizedGroupId
        : null,
  };
}

const ZONE_ORIENTATION: Record<Exclude<AronDropZone, "center">, AronSplitOrientation> = {
  left: "row",
  right: "row",
  top: "column",
  bottom: "column",
};

export interface AronWorkbenchState {
  /** The layout tree: one group, or splits of splits of groups. */
  root: AronNode;
  activeGroupId: string;
  /** When set, only this group is rendered (VS Code "maximize editor group"). */
  maximizedGroupId: string | null;
  sidebarOpen: boolean;
  /** Master switch for the write-approval modal (the inline card always stays). */
  approvalModalEnabled: boolean;

  /** convId → what ARON wants, raised while the tab was in the background. */
  attention: Record<string, AronAttention>;
  /** The write currently held up in the approval modal. */
  approval: AronApprovalRequest | null;
  /** Write ids already shown in the modal — never pop for the same one twice. */
  approvalSeen: string[];
  /** The tab being dragged right now (drives the pane drop zones). */
  draggingTab: { groupId: string; key: string } | null;

  openConversation: (convId: string, opts?: { groupId?: string }) => void;
  /** Otwórz CUDZĄ rozmowę jako kartę podglądu (audyt CEO+DEV) — tylko do odczytu. */
  openAuditConversation: (
    convId: string,
    title: string,
    opts?: { groupId?: string },
  ) => void;
  openDraft: (groupId?: string) => void;
  activateTab: (groupId: string, key: string) => void;
  closeTab: (groupId: string, key: string) => void;
  closeOtherTabs: (groupId: string, key: string) => void;
  focusGroup: (groupId: string) => void;
  moveTab: (fromGroupId: string, key: string, toGroupId: string, index?: number) => void;
  /** Split this pane, showing `key` (default: its active tab) beside it — VS
   * Code's "Split editor right / down". */
  splitGroup: (groupId: string, orientation: AronSplitOrientation, key?: string) => void;
  /** Move a tab out of its group into a new pane beside it. */
  detachTabToNewGroup: (
    fromGroupId: string,
    key: string,
    orientation: AronSplitOrientation,
  ) => void;
  /** Drop a dragged tab onto a group's BODY: the centre moves it into that
   * group, an edge splits — a new pane on that side, holding the tab. */
  dropTabOnGroup: (
    fromGroupId: string,
    key: string,
    toGroupId: string,
    zone: AronDropZone,
  ) => void;
  closeGroup: (groupId: string) => void;
  toggleMaximize: (groupId: string) => void;
  setSizes: (sizes: Record<string, number>) => void;
  /** A draft tab's conversation was created server-side — bind it to the tab. */
  adoptConversation: (groupId: string, key: string, convId: string) => void;
  setSidebarOpen: (open: boolean) => void;
  setApprovalModalEnabled: (enabled: boolean) => void;
  setDraggingTab: (t: { groupId: string; key: string } | null) => void;

  raiseAttention: (convId: string, kind: AronAttention) => void;
  clearAttention: (convId: string) => void;
  /** A write proposal just landed — open the gate, unless the user already saw
   * this exact proposal or switched the modal off. */
  requestApproval: (req: AronApprovalRequest & { writeId: string }) => void;
  /** Open the gate on demand (clicking a "do zatwierdzenia" badge). */
  openApproval: (req: AronApprovalRequest) => void;
  closeApproval: () => void;
}

const initialGroup = makeGroup([makeTab(null)]);

export const useAronWorkbenchStore = create<AronWorkbenchState>()(
  persist(
    (set, get) => ({
      root: initialGroup,
      activeGroupId: initialGroup.id,
      maximizedGroupId: null,
      sidebarOpen: true,
      approvalModalEnabled: true,
      attention: {},
      approval: null,
      approvalSeen: [],
      draggingTab: null,

      openConversation: (convId, opts = {}) =>
        set((s) => {
          const attention = { ...s.attention };
          delete attention[convId];
          // Already open somewhere: focus it instead of opening a second copy.
          // Karta PODGLĄDU tej samej rozmowy się nie liczy — to inny widok
          // (cudzy wątek tylko do odczytu), nie ta sama karta czatu.
          for (const g of collectGroups(s.root)) {
            const hit = g.tabs.find((t) => t.convId === convId && !t.readOnly);
            if (hit) {
              return {
                attention,
                activeGroupId: g.id,
                root: updateGroup(s.root, g.id, (x) => ({ ...x, activeKey: hit.key })),
              };
            }
          }
          const target =
            findGroup(s.root, opts.groupId ?? s.activeGroupId) ?? collectGroups(s.root)[0];
          const tab = makeTab(convId);
          return {
            attention,
            activeGroupId: target.id,
            root: updateGroup(s.root, target.id, (g) => {
              // Reuse the empty draft tab rather than stacking a second
              // "Nowa rozmowa" next to it.
              const draft = g.tabs.find((t) => t.convId === null && t.key === g.activeKey);
              if (draft) {
                return {
                  ...g,
                  tabs: g.tabs.map((t) => (t.key === draft.key ? { ...t, convId } : t)),
                  activeKey: draft.key,
                };
              }
              return { ...g, tabs: [...g.tabs, tab], activeKey: tab.key };
            }),
          };
        }),

      openAuditConversation: (convId, title, opts = {}) =>
        set((s) => {
          // Ta sama rozmowa już otwarta w podglądzie — pokaż tamtą kartę.
          for (const g of collectGroups(s.root)) {
            const hit = g.tabs.find((t) => t.convId === convId && t.readOnly);
            if (hit) {
              return {
                activeGroupId: g.id,
                root: updateGroup(s.root, g.id, (x) => ({ ...x, activeKey: hit.key })),
              };
            }
          }
          const target =
            findGroup(s.root, opts.groupId ?? s.activeGroupId) ?? collectGroups(s.root)[0];
          // Karta podglądu nigdy nie zjada pustego szkicu: szkic służy do pisania,
          // a w cudzej rozmowie pisać nie wolno.
          const tab = makeTab(convId, { readOnly: true, title });
          return {
            activeGroupId: target.id,
            root: updateGroup(s.root, target.id, (g) => ({
              ...g,
              tabs: [...g.tabs, tab],
              activeKey: tab.key,
            })),
          };
        }),

      openDraft: (groupId) =>
        set((s) => {
          const targetId = groupId ?? s.activeGroupId;
          const tab = makeTab(null);
          return {
            activeGroupId: targetId,
            root: updateGroup(s.root, targetId, (g) => ({
              ...g,
              tabs: [...g.tabs, tab],
              activeKey: tab.key,
            })),
          };
        }),

      activateTab: (groupId, key) =>
        set((s) => {
          const convId = findGroup(s.root, groupId)?.tabs.find((t) => t.key === key)?.convId;
          const attention = { ...s.attention };
          if (convId) delete attention[convId];
          return {
            attention,
            activeGroupId: groupId,
            root: updateGroup(s.root, groupId, (g) => ({ ...g, activeKey: key })),
          };
        }),

      closeTab: (groupId, key) =>
        set((s) =>
          finalize(
            updateGroup(s.root, groupId, (g) => {
              const i = g.tabs.findIndex((t) => t.key === key);
              if (i === -1) return g;
              const tabs = g.tabs.filter((t) => t.key !== key);
              // Closing the active tab activates its neighbour (right, else left).
              const activeKey =
                g.activeKey === key ? (tabs[i] ?? tabs[i - 1])?.key ?? null : g.activeKey;
              return { ...g, tabs, activeKey };
            }),
            s.activeGroupId,
            s.maximizedGroupId,
          ),
        ),

      closeOtherTabs: (groupId, key) =>
        set((s) =>
          finalize(
            updateGroup(s.root, groupId, (g) => ({
              ...g,
              tabs: g.tabs.filter((t) => t.key === key),
              activeKey: key,
            })),
            s.activeGroupId,
            s.maximizedGroupId,
          ),
        ),

      focusGroup: (groupId) =>
        set((s) => (findGroup(s.root, groupId) ? { activeGroupId: groupId } : {})),

      moveTab: (fromGroupId, key, toGroupId, index) =>
        set((s) => {
          const from = findGroup(s.root, fromGroupId);
          const tab = from?.tabs.find((t) => t.key === key);
          if (!tab) return {};
          if (fromGroupId === toGroupId) {
            return {
              activeGroupId: toGroupId,
              root: updateGroup(s.root, toGroupId, (g) => {
                const rest = g.tabs.filter((t) => t.key !== key);
                const at = index === undefined ? rest.length : Math.min(index, rest.length);
                return {
                  ...g,
                  tabs: [...rest.slice(0, at), tab, ...rest.slice(at)],
                  activeKey: key,
                };
              }),
            };
          }
          let root = updateGroup(s.root, fromGroupId, (g) => {
            const tabs = g.tabs.filter((t) => t.key !== key);
            return {
              ...g,
              tabs,
              activeKey:
                g.activeKey === key ? tabs[tabs.length - 1]?.key ?? null : g.activeKey,
            };
          });
          root = updateGroup(root, toGroupId, (g) => {
            const at = index === undefined ? g.tabs.length : Math.min(index, g.tabs.length);
            return {
              ...g,
              tabs: [...g.tabs.slice(0, at), tab, ...g.tabs.slice(at)],
              activeKey: key,
            };
          });
          return finalize(root, toGroupId, s.maximizedGroupId);
        }),

      splitGroup: (groupId, orientation, key) =>
        set((s) => {
          const src = findGroup(s.root, groupId);
          if (!src) return {};
          const srcTab = src.tabs.find((t) => t.key === (key ?? src.activeKey));
          // A draft (nothing created yet) cannot be shown twice — the copy would
          // be a second, unrelated new conversation. Split with a fresh draft.
          const group = makeGroup([makeTab(srcTab?.convId ?? null)]);
          return finalize(
            insertBeside(s.root, groupId, group, orientation, false),
            group.id,
            null,
          );
        }),

      detachTabToNewGroup: (fromGroupId, key, orientation) =>
        set((s) => {
          const from = findGroup(s.root, fromGroupId);
          const tab = from?.tabs.find((t) => t.key === key);
          if (!tab || !from || from.tabs.length < 2) return {};
          const group = makeGroup([tab]);
          const stripped = updateGroup(s.root, fromGroupId, (g) => ({
            ...g,
            tabs: g.tabs.filter((t) => t.key !== key),
          }));
          return finalize(
            insertBeside(stripped, fromGroupId, group, orientation, false),
            group.id,
            null,
          );
        }),

      dropTabOnGroup: (fromGroupId, key, toGroupId, zone) => {
        if (zone === "center") {
          get().moveTab(fromGroupId, key, toGroupId);
          return;
        }
        set((s) => {
          const from = findGroup(s.root, fromGroupId);
          const tab = from?.tabs.find((t) => t.key === key);
          if (!tab || !from) return {};
          // Tearing the only tab out of its own group and dropping it back next
          // to itself would just shuffle ids — nothing would change on screen.
          if (from.id === toGroupId && from.tabs.length < 2) return {};
          const group = makeGroup([tab]);
          const rest = from.tabs.filter((t) => t.key !== key);
          const stripped = updateGroup(s.root, fromGroupId, (g) => ({
            ...g,
            tabs: rest,
            activeKey: g.activeKey === key ? rest[rest.length - 1]?.key ?? null : g.activeKey,
          }));
          return finalize(
            insertBeside(
              stripped,
              toGroupId,
              group,
              ZONE_ORIENTATION[zone],
              zone === "left" || zone === "top",
            ),
            group.id,
            null,
          );
        });
      },

      closeGroup: (groupId) =>
        set((s) =>
          finalize(
            removeGroup(s.root, groupId),
            s.activeGroupId === groupId ? "" : s.activeGroupId,
            s.maximizedGroupId,
          ),
        ),

      toggleMaximize: (groupId) =>
        set((s) => ({
          maximizedGroupId: s.maximizedGroupId === groupId ? null : groupId,
          activeGroupId: groupId,
        })),

      setSizes: (sizes) => set((s) => ({ root: applySizes(s.root, sizes) })),

      adoptConversation: (groupId, key, convId) =>
        set((s) => ({
          root: updateGroup(s.root, groupId, (g) => ({
            ...g,
            tabs: g.tabs.map((t) => (t.key === key ? { ...t, convId } : t)),
          })),
        })),

      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      setApprovalModalEnabled: (approvalModalEnabled) => set({ approvalModalEnabled }),
      setDraggingTab: (draggingTab) => set({ draggingTab }),

      raiseAttention: (convId, kind) =>
        set((s) =>
          s.attention[convId] === kind
            ? {}
            : { attention: { ...s.attention, [convId]: kind } },
        ),

      clearAttention: (convId) =>
        set((s) => {
          if (!(convId in s.attention)) return {};
          const attention = { ...s.attention };
          delete attention[convId];
          return { attention };
        }),

      requestApproval: ({ convId, writeId, apiBase }) => {
        const s = get();
        // A one-shot gate per proposal: never re-pops for a write the user
        // already saw (and postponed), never queue-jumps a decision already on
        // screen, and stays out of the way entirely when switched off.
        if (!s.approvalModalEnabled) return;
        if (s.approvalSeen.includes(writeId)) return;
        set({
          approvalSeen: [...s.approvalSeen, writeId].slice(-200),
          approval: s.approval ?? { convId, apiBase },
        });
      },

      openApproval: (approval) => set({ approval }),

      closeApproval: () => set({ approval: null }),
    }),
    {
      name: "elg5-aron-workbench",
      version: 2,
      // Layout is the session; signals are live state and must not survive a reload.
      partialize: (s) => ({
        root: s.root,
        activeGroupId: s.activeGroupId,
        sidebarOpen: s.sidebarOpen,
        approvalModalEnabled: s.approvalModalEnabled,
      }),
      // v1 kept a flat group list plus one global orientation — wrap it in a split.
      migrate: (persisted, version) => {
        const p = (persisted ?? {}) as Record<string, unknown>;
        if (version >= 2) return p;
        const legacy = ((p.groups as AronGroupNode[] | undefined) ?? [])
          .filter((g) => Array.isArray(g?.tabs) && g.tabs.length > 0)
          .map((g) => ({ ...g, type: "group" as const, size: g.size || 1 }));
        const root: AronNode | null =
          legacy.length === 0
            ? null
            : legacy.length === 1
              ? legacy[0]
              : {
                  type: "split",
                  id: uid("s"),
                  orientation: (p.orientation as AronSplitOrientation) || "row",
                  size: 1,
                  children: legacy,
                };
        return { ...p, ...finalize(root, String(p.activeGroupId ?? ""), null) };
      },
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<AronWorkbenchState>;
        const merged = { ...current, ...p } as AronWorkbenchState;
        return {
          ...merged,
          ...finalize(merged.root ?? null, merged.activeGroupId, null),
          attention: {},
          approval: null,
          approvalSeen: [],
          draggingTab: null,
        };
      },
    },
  ),
);
